import { Badge } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormModal } from '../../../components/FormModal'
import { IParsedPlace } from '../../../components/googleMaps/AddressAutocomplete'
import { useZipCodeLookup } from '../../../components/MaskedInput'
import { getApiErrorMessage, slugify, useAppToast } from '../../../helpers'
import { CoursesService } from '../../../services/CoursesService/CoursesService'
import { ISelectCourse } from '../../../services/CoursesService/interfaces'
import { EventService, IEventFields, IProfessionalFields } from '../../../services/Event/EventService'
import {
  EventFormat,
  EventType,
  EventComplete,
  ParkingTicketType,
  TicketType,
  ITestimonialSlot
} from '../../../services/Event/types'
import { TeacherService } from '../../../services/Teacher/TeacherService'
import { SelectTeacher } from '../../../services/Teacher/types'
import { CaucaoInfoModal } from './CaucaoInfoModal'
import { establishmentFromApi, withoutIds } from './establishments/establishmentForm'
import { ContentTab } from './EventFormTabs/ContentTab'
import { EngagementTab } from './EventFormTabs/EngagementTab'
import { EstablishmentsTab } from './EventFormTabs/EstablishmentsTab'
import { IEventForm, ITicketForm } from './EventFormTabs/eventFormShared'
import { FaqTab } from './EventFormTabs/FaqTab'
import { GeneralTab } from './EventFormTabs/GeneralTab'
import { LocationTab } from './EventFormTabs/LocationTab'
import { MarketingTab } from './EventFormTabs/MarketingTab'
import { parkingTicketErrorMessage, parkingTicketNotice } from './EventFormTabs/parkingTicket'
import { ProfessionalsTab } from './EventFormTabs/ProfessionalsTab'
import { TicketsTab } from './EventFormTabs/TicketsTab'
import { EVENT_TAB_DEFS, countErrorsPerTab, firstTabWithErrors, unmappedErrorMessages } from './EventFormTabs/types'
import { validateFaqs } from './FaqEditor'
import { allowsHelpForm } from './helpForm'

interface IProps {
  isOpen: boolean
  onClose: () => void
  event?: EventComplete
  duplicate?: boolean
}

const baseTicket = (): ITicketForm => ({
  type: 'PAID',
  title: '',
  quantity: '',
  price: 0,
  sales_start: '',
  sales_end: '',
  max_per_purchase: '',
  description: '',
  visible: true,
  fee_exempt: false,
  is_child_ticket: false,
  sell_at_door: false,
  door_price: null
})

const baseProfessional = (): IProfessionalFields => ({
  _id: crypto.randomUUID(),
  teacher_id: null,
  title: '',
  name: '',
  bio: '',
  thumb: null,
  thumbPreview: null
})

const baseForm: IEventForm = {
  name: '',
  slug: '',
  format: '',
  types: [],
  course_ids: [],
  thumb: null,
  thumb_wide: null,
  video: null,
  remove_video: false,
  video_url: null,
  date_start: '',
  is_multiday: false,
  date_end: '',
  time_start: '',
  time_end: '',
  visible: true,
  use_nfc: false,
  tickets: [],
  copy: '',
  target_audience: '',
  topics: '',
  professionals: [],
  zipcode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  latitude: null,
  longitude: null,
  place_id: null,
  place_name: null,
  min_installment_value: null,
  parking_ticket_type_id: null,
  use_term: false,
  term: null,
  use_testimonial: false,
  testimonial_title: null,
  testimonials: [],
  active_campaign_lead_lists: [],
  active_campaign_buyer_lists: [],
  use_help_form: false,
  faqs: [],
  inherited_faqs: [],
  establishments: []
}

const buildFormFromEvent = (event: EventComplete): IEventForm => ({
  name: event.name,
  slug: event.slug,
  format: event.format,
  types: event.types?.map((t) => t.id) ?? [],
  course_ids: event.courses?.map((c) => c.id) ?? [],
  thumb: null,
  thumb_wide: null,
  video: null,
  remove_video: false,
  video_url: event.video_url ?? null,
  date_start: event.date_start ? event.date_start.slice(0, 10) : '',
  is_multiday: event.is_multiday,
  date_end: event.date_end ? event.date_end.slice(0, 10) : '',
  time_start: event.time_start ?? '',
  time_end: event.time_end ?? '',
  visible: event.visible,
  use_nfc: event.use_nfc ?? false,
  copy: event.copy || '',
  target_audience: event.target_audience || '',
  topics: event.topics || '',
  professionals:
    event.professionals && event.professionals.length > 0
      ? event.professionals.map((p) => ({
          _id: crypto.randomUUID(),
          teacher_id: p.teacher_id ?? null,
          title: p.title,
          name: p.name,
          bio: p.bio || '',
          thumb: null,
          thumbPreview: p.thumb ?? null
        }))
      : [],
  tickets:
    event.tickets && event.tickets.length > 0
      ? event.tickets.map((t) => ({
          id: t.id,
          type: t.type,
          title: t.title,
          quantity: t.quantity,
          price: Number(t.price),
          sales_start: t.sales_start ? t.sales_start.slice(0, 16) : '',
          sales_end: t.sales_end ? t.sales_end.slice(0, 16) : '',
          max_per_purchase: t.max_per_purchase,
          description: t.description || '',
          visible: t.visible,
          fee_exempt: t.fee_exempt ?? false,
          is_child_ticket: t.is_child_ticket ?? false,
          sell_at_door: t.sell_at_door ?? false,
          door_price: t.door_price != null ? Number(t.door_price) : null
        }))
      : [],
  zipcode: event.zipcode || '',
  street: event.street || '',
  number: event.number || '',
  complement: event.complement || '',
  neighborhood: event.neighborhood || '',
  city: event.city || '',
  state: event.state || '',
  latitude: event.latitude ?? null,
  longitude: event.longitude ?? null,
  place_id: event.place_id ?? null,
  place_name: event.place_name ?? null,
  min_installment_value: event.min_installment_value ?? null,
  parking_ticket_type_id: event.parking_ticket_type_id ?? null,
  use_term: event.use_term ?? false,
  term: event.term ?? null,
  use_testimonial: event.use_testimonial ?? false,
  testimonial_title: event.testimonial_title ?? null,
  testimonials:
    event.testimonial_urls && event.testimonial_urls.length > 0
      ? event.testimonial_urls.map((url) => ({ _id: crypto.randomUUID(), file: null, existingUrl: url }))
      : [],
  active_campaign_lead_lists: event.active_campaign_lead_lists ?? [],
  active_campaign_buyer_lists: event.active_campaign_buyer_lists ?? [],
  use_help_form: event.use_help_form ?? false,
  faqs:
    event.faqs?.map((f) => ({
      _id: crypto.randomUUID(),
      id: f.id,
      question: f.question,
      answer: f.answer
    })) ?? [],
  inherited_faqs: event.inherited_faqs ?? [],
  establishments: event.establishments?.map(establishmentFromApi) ?? []
})

const buildDuplicateFormFromEvent = (event: EventComplete): IEventForm => {
  const form = buildFormFromEvent(event)

  return {
    ...form,
    name: `${event.name}_cópia`,
    slug: slugify(`${event.slug}-copia`),
    thumb: null,
    thumb_wide: null,
    parking_ticket_type_id: null,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tickets: buildFormFromEvent(event).tickets.map(({ id: _id, ...ticket }) => ticket),
    // Copy FAQs as brand-new rows (drop ids); a duplicate is standalone, no inherited FAQs.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    faqs: form.faqs.map(({ id: _id, ...faq }) => ({ ...faq, _id: crypto.randomUUID() })),
    inherited_faqs: [],
    // A cópia nasce sem vendas: os estabelecimentos viram linhas novas.
    establishments: form.establishments.map(withoutIds)
  }
}

const needsLocation = (format: EventFormat | '') => format === 'IN_PERSON' || format === 'HYBRID'

export function EventFormModal({ isOpen, onClose, event, duplicate }: IProps) {
  const service = new EventService()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  // A edição fica aberta depois de salvar (ABRA-399) e passa a valer o evento que a API devolveu:
  // é dele que vêm os ids das linhas criadas agora e as URLs das imagens enviadas.
  const [savedEvent, setSavedEvent] = useState(event)
  const isEditing = !!event && !duplicate
  const [form, setForm] = useState<IEventForm>(() =>
    event ? (duplicate ? buildDuplicateFormFromEvent(event) : buildFormFromEvent(event)) : baseForm
  )
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})
  // The form is split across tabs, so a failed save has to point at the offending one.
  const [tabIndex, setTabIndex] = useState(0)
  const { loadingZipCode, lookup: lookupZipCode } = useZipCodeLookup({
    onAddressFound: (address) => setForm((prev) => ({ ...prev, ...address })),
    onCoordinatesFound: (latitude, longitude) => setForm((prev) => ({ ...prev, latitude, longitude }))
  })
  // An edition that does not override the `slug` group has its URL derived from the
  // series, and the API rewrites it on every save — so editing it here is pointless.
  // Duplicating always creates a standalone event, which owns its URL again.
  const slugOwnedBySeries =
    !!savedEvent && !duplicate && !!savedEvent.event_series_id && !(savedEvent.overrides ?? []).includes('slug')
  // Cursos não têm override por edição: a série é a única fonte, e a API ignora
  // course_ids em eventos com event_series_id.
  const coursesOwnedBySeries = !!savedEvent && !duplicate && !!savedEvent.event_series_id
  const [caucaoInfoOpen, setCaucaoInfoOpen] = useState(false)
  // Sem ingresso pago no valor mínimo (ver ./helpForm) o switch fica travado, e o valor nunca
  // é enviado como true — a API recusa a combinação, aqui só evitamos o 422.
  const helpFormAllowed = allowsHelpForm(form.tickets)
  // "Local" only exists as a concept for in-person/hybrid events — mirrors the same
  // conditional the section itself used before the tab split. Filtered by `key` (like
  // EventSeriesFormModal's `hiddenTabKeys`), not array position — a `.slice(0, -1)` here
  // would silently hide the wrong tab the day EVENT_TAB_DEFS gains or reorders an entry.
  const hiddenTabKeys = new Set<string>(needsLocation(form.format) ? [] : ['location'])
  const visibleTabDefs = EVENT_TAB_DEFS.filter((tab) => !hiddenTabKeys.has(tab.key))
  const [eventTypes, setEventTypes] = useState<EventType[]>([])
  const [teachers, setTeachers] = useState<SelectTeacher[]>([])
  const [courses, setCourses] = useState<ISelectCourse[]>([])
  const [activeCampaignLists, setActiveCampaignLists] = useState<{ id: string; name: string }[]>([])
  const [parkingTicketTypes, setParkingTicketTypes] = useState<ParkingTicketType[]>([])
  // Começa em true: antes da lista chegar, o vínculo salvo não pode ser resolvido,
  // e sem isto o aviso de "ticket não veio na lista" piscava em todo evento
  // associado durante o carregamento — e travava o campo junto.
  const [parkingTicketsLoading, setParkingTicketsLoading] = useState(true)
  // False quando o ABRA Park não respondeu (a API avisa no envelope, ou a própria
  // chamada falhou). Aí a lista vazia não quer dizer "não há tickets".
  const [abraParkAvailable, setAbraParkAvailable] = useState(true)

  // O ticket associado, resolvido na lista carregada do ABRA Park.
  const selectedParkingTicket = parkingTicketTypes.find((t) => t.id === form.parking_ticket_type_id) ?? null

  // Há vínculo salvo, a lista já chegou e ele não está nela: ou o ABRA Park não
  // respondeu (aí o vínculo fica travado — não dá para escolher sobre uma lista
  // que não é a real), ou o ticket foi excluído por lá (aí o campo fica livre para
  // o usuário corrigir). Sem tratar isso, o select cairia no placeholder e diria
  // que não há ticket associado — e salvar reenviaria o id antigo em silêncio.
  const parkingTicketMissing =
    !parkingTicketsLoading && form.parking_ticket_type_id !== null && selectedParkingTicket === null

  // As duas causas, resolvidas num lugar só: o campo, o placeholder, o botão de desvincular e o
  // Salvar liam `parkingTicketMissing` combinado com `abraParkAvailable` cada um do seu jeito, e
  // dois deles já discordavam entre si.
  const parkingTicketDeleted = parkingTicketMissing && abraParkAvailable
  const parkingTicketUnverifiable = parkingTicketMissing && !abraParkAvailable
  const parkingTicketError = parkingTicketErrorMessage(form.parking_ticket_type_id, {
    deleted: parkingTicketDeleted,
    unverifiable: parkingTicketUnverifiable
  })

  // O bloqueio do Salvar precisa aparecer na aba: a mensagem é sintética e nunca passa pelo
  // `fieldErrors` da API, então sem isto o botão morria nas oito abas sem badge, tooltip nem
  // toast — e a explicação ficava numa aba que o usuário não está olhando.
  const errorCounts = countErrorsPerTab(
    visibleTabDefs,
    parkingTicketDeleted && !fieldErrors['parking_ticket_type_id']
      ? { ...fieldErrors, parking_ticket_type_id: parkingTicketError ?? [] }
      : fieldErrors
  )

  // Tickets do ABRA Park (ABRA-252). Falhar aqui não impede cadastrar o evento:
  // só deixa o campo de estacionamento sem opções — e, se havia vínculo, travado.
  // Efeito próprio, dependente do vínculo salvo, porque a API o recebe para
  // anexar o ticket que tenha saído da lista; o modal remonta por `key` com o
  // evento já carregado, então na prática roda uma vez.
  //
  // Numa duplicação não há vínculo salvo: o formulário nasce sem ticket
  // (buildDuplicateFormFromEvent), e mandar o id do evento original só anexaria
  // à lista um ticket que este formulário não tem como selecionar.
  const savedParkingTicketId = duplicate ? null : (savedEvent?.parking_ticket_type_id ?? null)
  useEffect(() => {
    // `savedParkingTicketId` muda também ao FECHAR uma duplicação — o EventPage desliga o
    // `duplicate` sem bumpar o `key`, então a mesma instância volta a apontar para o evento de
    // origem. Sem o `isActive` isso deixava uma resposta de um modal já fechado sobrescrever a
    // lista (e soltava um toast de erro em cima da listagem).
    let isActive = true

    setParkingTicketsLoading(true)
    setParkingTicketTypes([])
    setAbraParkAvailable(true)
    service
      .listParkingTicketTypes(savedParkingTicketId)
      .then((response) => {
        if (!isActive) return
        setParkingTicketTypes(response.data)
        setAbraParkAvailable(response.available)
      })
      .catch((error) => {
        if (!isActive) return
        setAbraParkAvailable(false)
        toast.error('Não foi possível carregar os tickets do ABRA Park', error)
      })
      .finally(() => {
        if (isActive) setParkingTicketsLoading(false)
      })

    return () => {
      isActive = false
    }
  }, [savedParkingTicketId])

  useEffect(() => {
    service.getTypes().then(setEventTypes)
    service
      .getActiveCampaignLists()
      .then(setActiveCampaignLists)
      .catch(() => toast.error('Não foi possível carregar as listas do Active Campaign'))
    new TeacherService().listForSelect().then(setTeachers)
    // Só presenciais e online ao vivo: curso gravado não tem página com carrossel
    // de eventos. O filtro da API é um OR inclusivo, então um curso que também
    // tenha versão gravada continua aparecendo pelas outras modalidades.
    new CoursesService()
      .listForSelect(true, true, false)
      .then(setCourses)
      .catch(() => toast.error('Não foi possível carregar a lista de cursos'))
  }, [])

  // Popula o formulário quando o evento muda (safety fallback)
  useEffect(() => {
    if (!event) return
    setSavedEvent(event)
    setForm(duplicate ? buildDuplicateFormFromEvent(event) : buildFormFromEvent(event))
    setTabIndex(0)
  }, [duplicate, event])

  // Pré-carrega as imagens do evento original ao duplicar
  useEffect(() => {
    if (!duplicate || !event) return

    const abortController = new AbortController()
    let isActive = true

    const showImageLoadWarning = (imageLabel: string) => {
      if (!isActive) return

      toast.warning(
        'Não foi possível duplicar uma imagem',
        `${imageLabel} não pôde ser carregada automaticamente. Revise a imagem antes de salvar o evento duplicado.`
      )
    }

    const loadImage = async (url: string, filename: string, signal: AbortSignal): Promise<File | null> => {
      try {
        const response = await fetch(url, { signal })

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`)
        }

        const blob = await response.blob()
        return new File([blob], filename, { type: blob.type })
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return null
        }

        throw error
      }
    }

    if (event.thumb) {
      loadImage(event.thumb, 'thumb', abortController.signal)
        .then((file) => {
          if (!isActive) return

          if (file) {
            setForm((prev) => ({ ...prev, thumb: file }))
            return
          }

          showImageLoadWarning('A imagem de capa')
        })
        .catch(() => {
          showImageLoadWarning('A imagem de capa')
        })
    }

    if (event.thumb_wide) {
      loadImage(event.thumb_wide, 'thumb_wide', abortController.signal)
        .then((file) => {
          if (!isActive) return

          if (file) {
            setForm((prev) => ({ ...prev, thumb_wide: file }))
            return
          }

          showImageLoadWarning('A imagem wide')
        })
        .catch(() => {
          showImageLoadWarning('A imagem wide')
        })
    }

    // Carrega imagens dos profissionais
    if (event.professionals && event.professionals.length > 0) {
      event.professionals.forEach((prof, index) => {
        if (prof.thumb) {
          loadImage(prof.thumb, `professional_${index}`, abortController.signal)
            .then((file) => {
              if (!isActive) return

              if (file) {
                setForm((prev) => ({
                  ...prev,
                  professionals: prev.professionals.map((p, i) => (i === index ? { ...p, thumb: file } : p))
                }))
                return
              }

              showImageLoadWarning(`A imagem do profissional ${index + 1}`)
            })

            .catch(() => {
              showImageLoadWarning(`A imagem do profissional ${index + 1}`)
            })
        }
      })
    }

    return () => {
      isActive = false
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicate, event])

  const handleZipcode = (zipcode: string) => {
    setForm((prev) => ({ ...prev, zipcode }))
    lookupZipCode(zipcode)
  }

  const handlePlaceSelected = (place: IParsedPlace) => {
    setForm((prev) => ({
      ...prev,
      street: place.street,
      number: place.number,
      neighborhood: place.neighborhood,
      city: place.city,
      state: place.state,
      zipcode: place.zipcode,
      latitude: place.latitude,
      longitude: place.longitude,
      place_id: place.place_id,
      place_name: place.place_name
    }))
  }

  const handleMapPositionChange = (lat: number, lng: number, clickedPlaceId?: string) => {
    setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }))

    if (!window.google?.maps) return

    if (clickedPlaceId) {
      const dummyElement = document.createElement('div')
      const placesService = new google.maps.places.PlacesService(dummyElement)

      placesService.getDetails({ placeId: clickedPlaceId, fields: ['name', 'address_components'] }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const components = place.address_components || []
          const get = (type: string, useShort = false) => {
            const comp = components.find((c) => c.types.includes(type))
            return comp ? (useShort ? comp.short_name : comp.long_name) : ''
          }

          setForm((prev) => ({
            ...prev,
            street: get('route'),
            number: get('street_number'),
            neighborhood: get('sublocality_level_1') || get('sublocality') || get('neighborhood'),
            city: get('administrative_area_level_2'),
            state: get('administrative_area_level_1', true),
            zipcode: get('postal_code')?.replace('-', '') || '',
            place_id: clickedPlaceId,
            place_name: place.name ?? null
          }))
        }
      })
      return
    }

    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        setForm((prev) => ({
          ...prev,
          place_id: null,
          place_name: null
        }))
        return
      }

      const components = results[0].address_components
      const get = (type: string, useShort = false) => {
        const comp = components.find((c) => c.types.includes(type))
        return comp ? (useShort ? comp.short_name : comp.long_name) : ''
      }

      setForm((prev) => ({
        ...prev,
        street: get('route'),
        number: get('street_number'),
        neighborhood: get('sublocality_level_1') || get('sublocality') || get('neighborhood'),
        city: get('administrative_area_level_2'),
        state: get('administrative_area_level_1', true),
        zipcode: get('postal_code')?.replace('-', '') || '',
        place_id: results[0].place_id ?? null,
        place_name: null
      }))
    })
  }

  const addTicket = (type: TicketType) => {
    setForm((prev) => ({
      ...prev,
      tickets: [...prev.tickets, { ...baseTicket(), type }]
    }))
  }

  const updateTicket = (index: number, field: keyof ITicketForm, value: unknown) => {
    setForm((prev) => {
      const tickets = [...prev.tickets]
      tickets[index] = { ...tickets[index], [field]: value }
      return { ...prev, tickets }
    })
  }

  const removeTicket = (index: number) => {
    setForm((prev) => ({
      ...prev,
      tickets: prev.tickets.filter((_, i) => i !== index)
    }))
  }

  const addExternalProfessional = () => {
    setForm((prev) => ({
      ...prev,
      professionals: [...prev.professionals, baseProfessional()]
    }))
  }

  const addTeacherProfessional = (teacherId: number) => {
    const teacher = teachers.find((t) => t.id === teacherId)
    if (!teacher) return
    const newProfessional: IProfessionalFields = {
      _id: crypto.randomUUID(),
      teacher_id: teacher.id,
      title: teacher.staff_body_text ?? '',
      name: teacher.name,
      bio: teacher.iam_text || '',
      thumb: null,
      thumbPreview: null
    }
    setForm((prev) => ({
      ...prev,
      professionals: [...prev.professionals, newProfessional]
    }))
  }

  const updateProfessional = (id: string, field: keyof IProfessionalFields, value: string) => {
    setForm((prev) => ({
      ...prev,
      professionals: prev.professionals.map((p) => (p._id === id ? { ...p, [field]: value } : p))
    }))
  }

  const removeProfessional = (id: string) => {
    setForm((prev) => ({
      ...prev,
      professionals: prev.professionals.filter((p) => p._id !== id)
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    setFieldErrors({})

    const faqErrors = validateFaqs(form.faqs)
    if (Object.keys(faqErrors).length > 0) {
      setFieldErrors(faqErrors)
      const offendingTab = firstTabWithErrors(visibleTabDefs, faqErrors)
      // -1 (no tab's field list matched) still jumps to Geral instead of leaving the
      // tab wherever it was — a forgotten field there is less confusing than the
      // toast pointing nowhere.
      setTabIndex(offendingTab >= 0 ? offendingTab : 0)
      setSaving(false)
      toast.error('Erro de validação', 'Preencha a pergunta e a resposta em todas as perguntas frequentes.')
      return
    }

    // Testimonials: submit by the same (visual) slot index the UI renders, so a 422 from
    // the API lands under the right slot. Empty slots become null entries the service
    // skips; the API keys errors by the index it receives and compacts the final list.
    const testimonialSlots = form.use_testimonial ? form.testimonials : []

    const payload: IEventFields = {
      ...form,
      tickets: form.tickets.map((ticket) => ({
        ...ticket,
        quantity: Number(ticket.quantity) || 0,
        max_per_purchase: Number(ticket.max_per_purchase) || 0
      })),
      // Um evento que perdeu o ingresso caro (ou nunca teve) volta com a opção desligada,
      // em vez de ser bloqueado pela API na hora de salvar.
      use_help_form: helpFormAllowed && form.use_help_form,
      testimonial_videos: testimonialSlots.map((s) => s.file),
      // A slot with a new file replaces the old one, so it must not also carry the kept
      // path — sending both would make the slot ambiguous for the API.
      testimonial_existing: testimonialSlots.map((s) => (s.file ? null : s.existingUrl))
    }

    const saveFunction = isEditing && savedEvent ? service.update(savedEvent.id, payload) : service.create(payload)

    saveFunction
      .then(() => {
        toast.success(isEditing ? 'Evento atualizado com sucesso' : 'Evento criado com sucesso')
        if (!isEditing || !savedEvent) {
          onClose()
          return
        }
        // A edição fica aberta, na mesma aba (ABRA-399). O formulário volta da API: uma linha criada
        // agora (ingresso, FAQ, produto) só ganha id assim, e sem ele o próximo Salvar a criaria de
        // novo. Por isso, se a recarga falhar, o modal fecha. O `saving` só solta depois dela.
        return service
          .get(savedEvent.id)
          .then((fresh) => {
            setSavedEvent(fresh)
            setForm(buildFormFromEvent(fresh))
          })
          .catch((error) => {
            toast.error('Evento salvo, mas não foi possível recarregar o formulário', error)
            onClose()
          })
      })
      .catch((err) => {
        if (err?.response?.status === 422 && err?.response?.data?.errors) {
          setFieldErrors(err.response.data.errors)
          const offendingTab = firstTabWithErrors(visibleTabDefs, err.response.data.errors)
          // -1 (no tab's field list matched) still jumps to Geral instead of leaving the
          // tab wherever it was — a forgotten field there is less confusing than the
          // toast pointing nowhere.
          setTabIndex(offendingTab >= 0 ? offendingTab : 0)
          // No tab has a field to show it on, so at least put the real message in
          // front of the user instead of a generic "corrija os erros" that points at
          // nothing.
          toast.error(
            'Erro de validação',
            offendingTab >= 0
              ? 'Corrija os erros indicados no formulário.'
              : unmappedErrorMessages(visibleTabDefs, err.response.data.errors).join(' ')
          )
          return
        }
        if (err?.response?.status === 422 && err?.response?.data?.message) {
          toast.warning('Operação não permitida', getApiErrorMessage(err))
          return
        }
        if (err?.response?.status === 400 && err?.response?.data?.errors) {
          setFieldErrors(err.response.data.errors)
          const offendingTab = firstTabWithErrors(visibleTabDefs, err.response.data.errors)
          // -1 (no tab's field list matched) still jumps to Geral instead of leaving the
          // tab wherever it was — a forgotten field there is less confusing than the
          // toast pointing nowhere.
          setTabIndex(offendingTab >= 0 ? offendingTab : 0)
          // No tab has a field to show it on, so at least put the real message in
          // front of the user instead of a generic "corrija os erros" that points at
          // nothing.
          toast.error(
            'Erro de validação',
            offendingTab >= 0
              ? 'Corrija os erros indicados no formulário.'
              : unmappedErrorMessages(visibleTabDefs, err.response.data.errors).join(' ')
          )
          return
        }
        toast.error(isEditing ? 'Erro ao atualizar evento' : 'Erro ao criar evento')
      })
      .finally(() => setSaving(false))
  }

  const addTestimonialSlot = () => {
    setForm((f) => ({
      ...f,
      testimonials: [...f.testimonials, { _id: crypto.randomUUID(), file: null, existingUrl: null }]
    }))
  }

  const updateTestimonialSlot = (index: number, patch: Partial<ITestimonialSlot>) => {
    setForm((f) => ({
      ...f,
      testimonials: f.testimonials.map((slot, i) => (i === index ? { ...slot, ...patch } : slot))
    }))
  }

  const removeTestimonialSlot = (index: number) => {
    setForm((f) => ({ ...f, testimonials: f.testimonials.filter((_, i) => i !== index) }))
    // Slot indices shift on removal, so drop the per-slot errors to avoid mismatches.
    setFieldErrors((prev) => {
      const next: Record<string, string[]> = {}
      Object.entries(prev).forEach(([key, value]) => {
        if (/^testimonial_(videos|existing)(\.\d+)?$/.test(key)) return
        next[key] = value
      })
      return next
    })
  }

  // Rótulo da aba com o contador de erros. O índice é o da lista visível, que é a mesma que
  // alimenta o array de abas — por isso rótulo e painel não têm como se desencontrar.
  const tabLabel = (index: number) => (
    <>
      {visibleTabDefs[index]?.label}
      {errorCounts[index] > 0 && (
        <Badge ml={2} colorScheme="red">
          {errorCounts[index]}
        </Badge>
      )}
    </>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={`${isEditing ? 'Editar' : 'Cadastrar'} Evento`}
        onSubmit={handleSave}
        // Um obrigatório vazio numa aba fechada (o CEP do Local, o nome de um estabelecimento)
        // travava o Salvar em silêncio: a API é quem valida e leva o erro até a aba.
        noValidate
        size="editor"
        isLoading={saving}
        // Salvar a edição não fecha o modal (ABRA-399): sair é por aqui ou pelo X.
        cancelLabel={isEditing ? 'Fechar' : undefined}
        // Só quando o ticket foi de fato excluído no ABRA Park (não quando ele só está fora
        // do ar): nesse segundo caso o vínculo antigo é mantido e salvar não o altera, então
        // travar o Salvar aqui contradiria o próprio aviso da aba de ingressos.
        isConfirmDisabled={parkingTicketDeleted}
        tabIndex={Math.min(tabIndex, visibleTabDefs.length - 1)}
        onTabChange={setTabIndex}
        tabs={[
          {
            label: tabLabel(0),
            content: (
              <GeneralTab
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
                saving={saving}
                event={savedEvent}
                eventTypes={eventTypes}
                courses={courses}
                slugOwnedBySeries={slugOwnedBySeries}
                coursesOwnedBySeries={coursesOwnedBySeries}
              />
            )
          },
          {
            label: tabLabel(1),
            content: (
              <TicketsTab
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
                saving={saving}
                addTicket={addTicket}
                updateTicket={updateTicket}
                removeTicket={removeTicket}
                setCaucaoInfoOpen={setCaucaoInfoOpen}
                parkingTicketTypes={parkingTicketTypes}
                selectedParkingTicket={selectedParkingTicket}
                parkingTicketsLoading={parkingTicketsLoading}
                parkingTicketDeleted={parkingTicketDeleted}
                parkingTicketUnverifiable={parkingTicketUnverifiable}
                parkingTicketError={parkingTicketError}
                parkingTicketNotice={parkingTicketNotice(abraParkAvailable, parkingTicketMissing)}
              />
            )
          },
          {
            label: tabLabel(2),
            content: <ContentTab form={form} setForm={setForm} fieldErrors={fieldErrors} saving={saving} />
          },
          {
            label: tabLabel(3),
            content: (
              <EngagementTab
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
                saving={saving}
                helpFormAllowed={helpFormAllowed}
                addTestimonialSlot={addTestimonialSlot}
                updateTestimonialSlot={updateTestimonialSlot}
                removeTestimonialSlot={removeTestimonialSlot}
              />
            )
          },
          {
            label: tabLabel(4),
            content: <FaqTab form={form} setForm={setForm} fieldErrors={fieldErrors} saving={saving} />
          },
          {
            label: tabLabel(5),
            content: (
              <MarketingTab
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
                saving={saving}
                activeCampaignLists={activeCampaignLists}
              />
            )
          },
          {
            label: tabLabel(6),
            content: (
              <ProfessionalsTab
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
                saving={saving}
                teachers={teachers}
                addExternalProfessional={addExternalProfessional}
                addTeacherProfessional={addTeacherProfessional}
                updateProfessional={updateProfessional}
                removeProfessional={removeProfessional}
              />
            )
          },
          {
            label: tabLabel(7),
            content: (
              <EstablishmentsTab
                form={form}
                setForm={setForm}
                fieldErrors={fieldErrors}
                setFieldErrors={setFieldErrors}
                saving={saving}
              />
            )
          },
          // A aba de local só existe nos formatos que têm endereço. Ela é a última do
          // `EVENT_TAB_DEFS`, então some do fim da lista sem deslocar os índices das outras.
          ...(needsLocation(form.format)
            ? [
                {
                  label: tabLabel(8),
                  content: (
                    <LocationTab
                      form={form}
                      setForm={setForm}
                      fieldErrors={fieldErrors}
                      saving={saving}
                      loadingZip={loadingZipCode}
                      handleZipcode={handleZipcode}
                      handlePlaceSelected={handlePlaceSelected}
                      handleMapPositionChange={handleMapPositionChange}
                    />
                  )
                }
              ]
            : [])
        ]}
      />

      <CaucaoInfoModal isOpen={caucaoInfoOpen} onClose={() => setCaucaoInfoOpen(false)} />
    </>
  )
}
